import { useRef, useState } from 'react'
import { usePlanEditor, type Plan } from './useServices.ts'

/**
 * A plan's title edited where it stands (the plan's header on a computer):
 * `start` turns the title into a field holding it, Enter or leaving the field
 * saves (`commit`), Escape puts it back (`cancel`). An emptied title clears
 * it, so the plan goes back to its service type's name. While the change is
 * on its way the header shows the new title, not the old one; a failure is
 * the plan's own (`SaveFailure`, with Retry).
 */
export function usePlanTitleEditor(plan: Plan) {
  const editor = usePlanEditor(plan.id)
  // null: not editing.
  const [draft, setDraft] = useState<string | null>(null)
  // Enter saves and the field goes: the blur that follows must not save again,
  // and the blur after Escape must not save at all.
  const settled = useRef(false)

  const start = () => {
    settled.current = false
    setDraft(plan.title ?? '')
  }
  const cancel = () => {
    settled.current = true
    setDraft(null)
  }
  const commit = () => {
    if (settled.current || draft === null) return
    settled.current = true
    setDraft(null)
    const next = draft.trim() || null
    if (next === (plan.title ?? null)) return
    editor.updatePlan.mutate({ title: next })
  }

  const sending = editor.updatePlan.isPending ? editor.updatePlan.variables : undefined
  const title = sending && 'title' in sending ? (sending.title ?? null) : (plan.title ?? null)
  return {
    editing: draft !== null,
    draft: draft ?? '',
    change: setDraft,
    start,
    cancel,
    commit,
    /** What the header shows: the title (the one being saved, while it is). */
    shown: title ?? plan.serviceTypeName,
    saving: editor.updatePlan.isPending,
  }
}
